import { useState, useMemo } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowDownRight,
  ArrowUpRight,
  Wallet,
  TrendingUp,
  Clock,
  CheckCircle2,
  Building2,
  Search,
  Filter,
  Download,
  ChevronLeft,
  ChevronRight,
  Eye,
  X,
  Calendar,
  RefreshCw,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface Transaction {
  id: string;
  type: "deposit" | "withdrawal";
  amount: number;
  status: "completed" | "pending" | "failed";
  time: string;
  description: string;
  customer?: string;
  bankAccount?: string;
}

const balance = {
  available: 1250000,
  pending: 150000,
  total: 1400000,
};

const allTransactions: Transaction[] = [
  {
    id: "TX-001",
    type: "deposit",
    amount: 50000,
    status: "completed",
    time: "2024-12-21 10:30",
    description: "Customer payment",
    customer: "John Doe",
  },
  {
    id: "TX-002",
    type: "withdrawal",
    amount: 100000,
    status: "completed",
    time: "2024-12-21 08:15",
    description: "Bank transfer",
    bankAccount: "First Bank ****6789",
  },
  {
    id: "TX-003",
    type: "deposit",
    amount: 75000,
    status: "pending",
    time: "2024-12-21 09:45",
    description: "Customer payment",
    customer: "Sarah Smith",
  },
  {
    id: "TX-004",
    type: "withdrawal",
    amount: 50000,
    status: "completed",
    time: "2024-12-20 14:20",
    description: "Bank transfer",
    bankAccount: "GTBank ****1234",
  },
  {
    id: "TX-005",
    type: "deposit",
    amount: 125000,
    status: "completed",
    time: "2024-12-20 11:00",
    description: "Customer payment",
    customer: "Mike Johnson",
  },
  {
    id: "TX-006",
    type: "deposit",
    amount: 35000,
    status: "completed",
    time: "2024-12-19 16:30",
    description: "Customer payment",
    customer: "Emma Davis",
  },
  {
    id: "TX-007",
    type: "withdrawal",
    amount: 200000,
    status: "failed",
    time: "2024-12-19 10:00",
    description: "Bank transfer failed",
    bankAccount: "First Bank ****6789",
  },
  {
    id: "TX-008",
    type: "deposit",
    amount: 89000,
    status: "completed",
    time: "2024-12-18 15:45",
    description: "Customer payment",
    customer: "David Wilson",
  },
  {
    id: "TX-009",
    type: "withdrawal",
    amount: 75000,
    status: "completed",
    time: "2024-12-18 09:30",
    description: "Bank transfer",
    bankAccount: "UBA ****5678",
  },
  {
    id: "TX-010",
    type: "deposit",
    amount: 150000,
    status: "pending",
    time: "2024-12-17 14:00",
    description: "Customer payment",
    customer: "Lisa Chen",
  },
  {
    id: "TX-011",
    type: "deposit",
    amount: 42000,
    status: "completed",
    time: "2024-12-17 11:30",
    description: "Customer payment",
    customer: "Robert Brown",
  },
  {
    id: "TX-012",
    type: "withdrawal",
    amount: 125000,
    status: "completed",
    time: "2024-12-16 16:00",
    description: "Bank transfer",
    bankAccount: "GTBank ****1234",
  },
];

export default function VendorWallet() {
  const navigate = useNavigate();
  const [isWithdrawDialogOpen, setIsWithdrawDialogOpen] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTransaction, setSelectedTransaction] =
    useState<Transaction | null>(null);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const itemsPerPage = 5;

  // Filter transactions
  const filteredTransactions = useMemo(() => {
    return allTransactions.filter((tx) => {
      const matchesSearch =
        tx.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tx.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (tx.customer &&
          tx.customer.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesType = typeFilter === "all" || tx.type === typeFilter;
      const matchesStatus =
        statusFilter === "all" || tx.status === statusFilter;

      return matchesSearch && matchesType && matchesStatus;
    });
  }, [searchQuery, typeFilter, statusFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const paginatedTransactions = filteredTransactions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handleWithdraw = () => {
    const amount = parseFloat(withdrawAmount);
    if (!amount || amount <= 0) {
      toast({
        title: "Invalid Amount",
        description: "Please enter a valid withdrawal amount.",
        variant: "destructive",
      });
      return;
    }
    if (amount > balance.available) {
      toast({
        title: "Insufficient Balance",
        description: "You do not have enough funds for this withdrawal.",
        variant: "destructive",
      });
      return;
    }
    toast({
      title: "Withdrawal Initiated",
      description: `₦${amount.toLocaleString()} withdrawal request has been submitted.`,
    });
    setIsWithdrawDialogOpen(false);
    setWithdrawAmount("");
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    setTimeout(() => {
      setIsRefreshing(false);
      toast({
        title: "Refreshed",
        description: "Transaction history has been updated.",
      });
    }, 500);
  };

  const handleExport = () => {
    const csvContent = [
      [
        "Transaction ID",
        "Type",
        "Amount",
        "Status",
        "Time",
        "Description",
      ].join(","),
      ...filteredTransactions.map((tx) =>
        [tx.id, tx.type, tx.amount, tx.status, tx.time, tx.description].join(
          ",",
        ),
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `transactions-export-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();

    toast({
      title: "Export Complete",
      description: `${filteredTransactions.length} transactions exported to CSV.`,
    });
  };

  const handleViewTransaction = (tx: Transaction) => {
    setSelectedTransaction(tx);
    setShowTransactionModal(true);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return (
          <Badge className="bg-emerald-500/20 text-emerald-500">
            <CheckCircle2 className="h-3 w-3 mr-1" /> Completed
          </Badge>
        );
      case "pending":
        return (
          <Badge className="bg-amber-500/20 text-amber-500">
            <Clock className="h-3 w-3 mr-1" /> Pending
          </Badge>
        );
      case "failed":
        return (
          <Badge className="bg-red-500/20 text-red-500">
            <X className="h-3 w-3 mr-1" /> Failed
          </Badge>
        );
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const totalDeposits = allTransactions
    .filter((t) => t.type === "deposit" && t.status === "completed")
    .reduce((acc, t) => acc + t.amount, 0);
  const totalWithdrawals = allTransactions
    .filter((t) => t.type === "withdrawal" && t.status === "completed")
    .reduce((acc, t) => acc + t.amount, 0);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Wallet & Earnings</h1>
        <p className="text-muted-foreground">
          Manage your funds and withdrawals
        </p>
      </div>

      {/* Balance Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="bg-gradient-to-br from-emerald-500 to-teal-600 text-foreground">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100">Available Balance</p>
                <p className="text-3xl font-bold">
                  ₦{balance.available.toLocaleString()}
                </p>
              </div>
              <Wallet className="h-10 w-10 text-emerald-100" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending</p>
                <p className="text-2xl font-bold">
                  ₦{balance.pending.toLocaleString()}
                </p>
              </div>
              <Clock className="h-8 w-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Earnings</p>
                <p className="text-2xl font-bold">
                  ₦{balance.total.toLocaleString()}
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-violet-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Deposits</p>
                <p className="text-xl font-bold text-emerald-500">
                  +₦{totalDeposits.toLocaleString()}
                </p>
              </div>
              <ArrowDownRight className="h-8 w-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  Total Withdrawals
                </p>
                <p className="text-xl font-bold text-amber-500">
                  -₦{totalWithdrawals.toLocaleString()}
                </p>
              </div>
              <ArrowUpRight className="h-8 w-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-4">
        <Dialog
          open={isWithdrawDialogOpen}
          onOpenChange={setIsWithdrawDialogOpen}
        >
          <DialogTrigger asChild>
            <Button className="bg-emerald-500 hover:bg-emerald-600">
              <ArrowDownRight className="mr-2 h-4 w-4" />
              Withdraw Funds
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Withdraw Funds</DialogTitle>
              <DialogDescription>
                Withdraw funds to your linked bank account
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">
                  Available Balance
                </p>
                <p className="text-2xl font-bold">
                  ₦{balance.available.toLocaleString()}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="amount">Withdrawal Amount</Label>
                <Input
                  id="amount"
                  type="number"
                  placeholder="Enter amount"
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Select Bank Account</Label>
                <div className="p-3 border rounded-lg flex items-center gap-3">
                  <Building2 className="h-5 w-5 text-emerald-500" />
                  <div>
                    <p className="font-medium">First Bank of Nigeria</p>
                    <p className="text-xs text-muted-foreground">
                      **** **** **** 6789
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsWithdrawDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleWithdraw}
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                Withdraw
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Button variant="outline" onClick={() => navigate("/vendor/banks")}>
          <Building2 className="mr-2 h-4 w-4" />
          View Bank Accounts
        </Button>
      </div>

      {/* Transactions */}
      <Card>
        <CardHeader>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <CardTitle>Transaction History</CardTitle>
              <CardDescription>Your recent wallet activity</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="p-2 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
              >
                <RefreshCw
                  className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`}
                />
              </button>
              <button
                onClick={handleExport}
                className="p-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Download className="w-4 h-4" />
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search transactions..."
                className="w-full pl-10 pr-4 py-2 bg-card border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
              />
            </div>
            <div className="flex items-center gap-3">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <select
                value={typeFilter}
                onChange={(e) => {
                  setTypeFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-4 py-2 bg-card border border-border rounded-lg text-sm text-foreground"
              >
                <option value="all">All Types</option>
                <option value="deposit">Deposits</option>
                <option value="withdrawal">Withdrawals</option>
              </select>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-4 py-2 bg-card border border-border rounded-lg text-sm text-foreground"
              >
                <option value="all">All Status</option>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
              </select>
            </div>
          </div>

          {/* Transactions List */}
          <div className="space-y-4">
            {paginatedTransactions.map((tx) => (
              <div
                key={tx.id}
                className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div
                    className={`h-10 w-10 rounded-full flex items-center justify-center ${
                      tx.type === "deposit"
                        ? "bg-emerald-500/20"
                        : "bg-amber-500/20"
                    }`}
                  >
                    {tx.type === "deposit" ? (
                      <ArrowDownRight className="h-5 w-5 text-emerald-500" />
                    ) : (
                      <ArrowUpRight className="h-5 w-5 text-amber-500" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium capitalize">{tx.type}</p>
                    <p className="text-sm text-muted-foreground">
                      {tx.description}
                    </p>
                    {tx.customer && (
                      <p className="text-xs text-muted-foreground">
                        From: {tx.customer}
                      </p>
                    )}
                    {tx.bankAccount && (
                      <p className="text-xs text-muted-foreground">
                        To: {tx.bankAccount}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {tx.id} • {tx.time}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p
                    className={`font-medium ${tx.type === "deposit" ? "text-emerald-500" : "text-amber-500"}`}
                  >
                    {tx.type === "deposit" ? "+" : "-"}₦
                    {tx.amount.toLocaleString()}
                  </p>
                  <div className="mt-1">{getStatusBadge(tx.status)}</div>
                  <button
                    onClick={() => handleViewTransaction(tx)}
                    className="mt-2 text-xs text-primary hover:underline flex items-center justify-end gap-1"
                  >
                    <Eye className="w-3 h-3" /> View
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
              <p className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                {Math.min(
                  currentPage * itemsPerPage,
                  filteredTransactions.length,
                )}{" "}
                of {filteredTransactions.length} transactions
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg bg-card text-muted-foreground disabled:opacity-50 hover:text-foreground transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                {Array.from(
                  { length: Math.min(5, totalPages) },
                  (_, i) => i + 1,
                ).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-1 rounded-lg text-sm ${
                      currentPage === page
                        ? "bg-primary text-primary-foreground"
                        : "bg-card text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg bg-card text-muted-foreground disabled:opacity-50 hover:text-foreground transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Transaction Detail Modal */}
      {showTransactionModal && selectedTransaction && (
        <Dialog
          open={showTransactionModal}
          onOpenChange={setShowTransactionModal}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Transaction Details</DialogTitle>
              <DialogDescription>
                Detailed information about this transaction
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="flex justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">Transaction ID</span>
                <span className="text-primary font-mono">
                  {selectedTransaction.id}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">Type</span>
                <span
                  className={`capitalize ${selectedTransaction.type === "deposit" ? "text-emerald-500" : "text-amber-500"}`}
                >
                  {selectedTransaction.type}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">Amount</span>
                <span
                  className={`font-bold ${selectedTransaction.type === "deposit" ? "text-emerald-500" : "text-amber-500"}`}
                >
                  {selectedTransaction.type === "deposit" ? "+" : "-"}₦
                  {selectedTransaction.amount.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">Status</span>
                {getStatusBadge(selectedTransaction.status)}
              </div>
              <div className="flex justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">Date & Time</span>
                <span className="text-foreground">
                  {selectedTransaction.time}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">Description</span>
                <span className="text-foreground">
                  {selectedTransaction.description}
                </span>
              </div>
              {selectedTransaction.customer && (
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Customer</span>
                  <span className="text-foreground">
                    {selectedTransaction.customer}
                  </span>
                </div>
              )}
              {selectedTransaction.bankAccount && (
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">Bank Account</span>
                  <span className="text-foreground">
                    {selectedTransaction.bankAccount}
                  </span>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button onClick={() => setShowTransactionModal(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
